import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import swal from "sweetalert";
import {
  createGovtOrder,
  updateGovtOrder,
} from "../../../services/adminApiService/govtOrderApi/govtOrderApi";

const GovtOrderModal = ({ order, onClose }) => {
  const [title, setTitle] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (order) {
      setTitle(order.title || "");
      setOrderDate(
        order.order_date ? order.order_date.split("T")[0] : ""
      );
      setSortOrder(order.sort_order || 0);
    }
  }, [order]);

  const handleFileChange = (e) => {
    if (!e.target.files.length) return;
    const file = e.target.files[0];
    if (!/\.pdf$/i.test(file.name)) {
      swal("Error", "Only PDF files are allowed.", "error");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      swal("Error", "File size must be under 10 MB.", "error");
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      swal("Error", "Title is required.", "error");
      return;
    }
    if (!order && !selectedFile) {
      swal("Error", "PDF file is required.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("title", title.trim());
    if (orderDate) formData.append("order_date", orderDate);
    formData.append("sort_order", sortOrder);
    if (selectedFile) formData.append("file", selectedFile);

    setLoading(true);
    try {
      if (order) {
        await updateGovtOrder(order.id, formData);
        swal("Success", "Government order updated.", "success");
      } else {
        await createGovtOrder(formData);
        swal("Success", "Government order created.", "success");
      }
      onClose(true);
    } catch (error) {
      const msg = error?.response?.data?.message || "Operation failed. Please try again.";
      swal("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show onHide={() => onClose(false)} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {order ? "Edit Government Order" : "Add Government Order"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Civil Services Competition"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Order Date</Form.Label>
            <Form.Control
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Sort Order</Form.Label>
            <Form.Control
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              min={0}
            />
            <Form.Text className="text-muted">
              Lower number = appears first in the list.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>
              PDF File{" "}
              {!order && <span className="text-danger">*</span>}
              {order && (
                <small className="text-muted ms-2">
                  (Leave empty to keep existing file)
                </small>
              )}
            </Form.Label>
            {order?.file_url && (
              <div className="mb-2">
                <a
                  href={order.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View current file {order.file_size && `(${order.file_size})`}
                </a>
              </div>
            )}
            <Form.Control
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
            />
            <Form.Text className="text-muted">PDF only. Max 10 MB.</Form.Text>
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="secondary"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : order ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default GovtOrderModal;
